import { useState } from "react";
import { Check } from "@phosphor-icons/react";
import WaitlistDialog from "@/components/WaitlistDialog";
import { Link } from "react-router-dom";

const TIERS = [
  {
    name: "Starter",
    price: "$999",
    period: "/mo",
    rate: "flat rate",
    tier: "Tier 4 · Up to 5 attorneys",
    features: [
      "Contract review + risk flagging",
      "Clause identification",
      "Contract repository (unlimited)",
      "Basic agentic workflows (3)",
      "Email support",
    ],
    cta: { label: "Start with Starter", to: "/contact" },
    highlighted: false,
  },
  {
    name: "Growth",
    price: "$1,499",
    period: "/mo",
    rate: "flat rate",
    tier: "Tier 3 · 5–25 attorneys",
    badge: "Most popular",
    features: [
      "Everything in Starter",
      "Contract comparison (unlimited)",
      "Custom playbooks (unlimited)",
      "Advanced agentic workflows (10)",
      "Dedicated onboarding",
      "Priority support",
    ],
    cta: { label: "Choose Growth", to: "/contact" },
    highlighted: true,
  },
  {
    name: "Firm",
    price: "Custom",
    period: "",
    rate: "annual contract",
    tier: "Tier 2 · 25–200 attorneys",
    features: [
      "Everything in Growth",
      "Unlimited agentic workflows",
      "Practice group dashboards",
      "API access + integrations",
      "SLA + dedicated CSM",
      "SSO included",
    ],
    cta: { label: "Contact enterprise", to: "/contact" },
    highlighted: false,
  },
];

export default function Pricing() {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="pricing-page">
      <section className="border-b border-border">
        <div className="container-x py-20 md:py-28 max-w-4xl">
          <p className="overline">Pricing</p>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tighter leading-[0.98] mt-5 font-light">
            Transparent pricing. <span className="italic text-[#002147]">No surprises.</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground">
            Flat-rate plans built for the way modern law firms actually buy software.
            Every plan includes contract review, clause identification, and an unlimited repository.
          </p>
        </div>
      </section>

      {/* MEASURABLE ROI */}
      <section data-testid="pricing-roi" className="border-b border-border bg-secondary/40">
        <div className="container-x py-20 md:py-24">
          <div className="max-w-3xl">
            <p className="overline">The numbers</p>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 tracking-tighter leading-[1.02]">
              Measurable <span className="italic text-[#002147]">ROI.</span>
            </h2>
            <p className="mt-5 text-base text-muted-foreground max-w-2xl leading-relaxed">
              Every ContractIQ deployment is measured against the metrics that matter to partners and GCs —
              cycle time, cost, and risk. Here&apos;s what firms see, on average, within the first year.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
            {[
              { stat: "70%", label: "Reduction in contract drafting time" },
              { stat: "3×", label: "Faster contract cycle time vs. manual process" },
              { stat: "85%", label: "Fewer missed renewal deadlines with auto-alerts" },
              { stat: "60%", label: "Reduction in legal review costs within 12 months" },
            ].map((r, i) => (
              <div key={i} className="bg-white p-8 md:p-10 relative">
                <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#002147]" aria-hidden />
                <div className="font-serif text-5xl md:text-6xl tracking-tighter font-light text-[#002147]">
                  {r.stat}
                </div>
                <div className="mt-4 h-px w-10 bg-border" />
                <p className="mt-4 text-sm text-foreground/80 leading-relaxed">{r.label}</p>
              </div>
            ))}
          </div>

          <p className="mt-6 text-xs font-mono tracking-[0.1em] text-muted-foreground">
            Based on design-partner engagements across mid-market firms · 12-month baselines.
          </p>
        </div>
      </section>

      <section className="container-x py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
          {TIERS.map((t, i) => (
            <div
              key={i}
              data-testid={`pricing-tier-${t.name.toLowerCase()}`}
              className={`flex flex-col p-10 ${t.highlighted ? "bg-[#002147] text-white" : "bg-white"}`}
            >
              <div className="flex items-center justify-between">
                <div className={`overline ${t.highlighted ? "text-white/80" : ""}`}>{t.name}</div>
                {t.badge && (
                  <span
                    data-testid="pricing-most-popular-badge"
                    className="font-mono text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 bg-white text-[#002147]"
                  >
                    {t.badge}
                  </span>
                )}
              </div>

              <div className="mt-8 flex items-baseline gap-1">
                <span className="font-serif text-5xl md:text-6xl tracking-tighter font-light">{t.price}</span>
                {t.period && (
                  <span className={`font-serif text-2xl ${t.highlighted ? "text-white/70" : "text-muted-foreground"}`}>
                    {t.period}
                  </span>
                )}
              </div>
              <p className={`mt-2 text-sm font-mono tracking-[0.05em] ${t.highlighted ? "text-white/70" : "text-muted-foreground"}`}>
                {t.rate}
              </p>

              <p className={`mt-6 overline ${t.highlighted ? "text-white/80" : ""}`}>{t.tier}</p>

              <div className={`mt-6 border-t ${t.highlighted ? "border-white/15" : "border-border"}`} />

              <ul className="mt-6 space-y-3 flex-1">
                {t.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-3 text-sm">
                    <Check size={16} weight="bold" className={`mt-0.5 ${t.highlighted ? "text-white" : "text-[#002147]"}`} />
                    <span className={t.highlighted ? "text-white/90" : "text-foreground/85"}>{f}</span>
                  </li>
                ))}
              </ul>

              <Link
                to={t.cta.to}
                data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                className={`mt-10 inline-flex items-center justify-center w-full px-6 py-3.5 text-sm font-medium transition-colors ${
                  t.highlighted
                    ? "bg-white text-[#002147] hover:bg-[#94A3B8] hover:text-[#0A0A0A]"
                    : "bg-[#0A0A0A] text-white hover:bg-[#002147]"
                }`}
              >
                {t.cta.label}
              </Link>
            </div>
          ))}
        </div>

        <p className="mt-8 text-sm text-muted-foreground italic font-serif text-center">
          All plans on limited beta · structured onboarding available for tier-2 firms.
        </p>

        <div className="mt-16 border border-border p-8 md:p-12 bg-secondary/40">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <p className="overline">Not ready to talk pricing?</p>
              <h3 className="font-serif text-3xl md:text-4xl mt-3 tracking-tight">
                Join the early-access waitlist instead.
              </h3>
            </div>
            <div className="md:col-span-4">
              <button
                data-testid="pricing-waitlist-btn"
                onClick={() => setOpen(true)}
                className="inline-flex items-center justify-center w-full px-6 py-3.5 bg-[#0A0A0A] text-white text-sm font-medium hover:bg-[#002147] transition-colors"
              >
                Join the waitlist
              </button>
            </div>
          </div>
        </div>
      </section>

      <WaitlistDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
